/**
 * CreditsContext - Provides global credits state management
 * Wraps useCredits hook in a context for component access
 */

import { createContext, useContext, ReactNode } from 'react';
import { useCredits, UseCreditsReturn } from '../hooks/useCredits';

/**
 * Credits context type
 */
type CreditsContextType = UseCreditsReturn;

/**
 * Credits context
 */
const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

/**
 * Props for CreditsProvider
 */
interface CreditsProviderProps {
  /** Child components */
  children: ReactNode;
}

/**
 * CreditsProvider component
 * Provides credit state and actions to all child components
 * Requires AuthProvider and UserProfileProvider to be parent components
 * 
 * @param props - Provider props
 * @returns Provider component
 */
export function CreditsProvider({ children }: CreditsProviderProps) {
  const creditsState = useCredits();

  return (
    <CreditsContext.Provider value={creditsState}>
      {children}
    </CreditsContext.Provider>
  );
}

/**
 * Hook to access credits context
 * Must be used within CreditsProvider
 * 
 * @returns Credit state and actions
 * @throws Error if used outside CreditsProvider
 */
export function useCreditsContext(): CreditsContextType {
  const context = useContext(CreditsContext);
  
  if (context === undefined) {
    throw new Error('useCreditsContext must be used within CreditsProvider');
  }
  
  return context;
}
